import store from "../store";
import {getCurrentLayout} from "../bvcomponents/appLayout";
import {getImagePromiseFromLayout} from "./getCanvasImageFromLayout";
import {getQuoteVisualizerType, switchToNewVisualizerType} from "./visualizerSwitchingFunctions";
import {getRoomImageFromQtCategory} from "./categorySwitchingFunctions";
import {setQuotes} from "../actions/roomsFunctions";
import {loaderDisable, loaderEnable} from "../bvcomponents/ui/loaderView";
import {getTotalPrice} from "./helpers";
import {setVisualizerIsChanging} from "../actions/serverFunctions";
import {switchToWizardItem} from "../bvcomponents/qkbPages/materialsWizard/useChangeWizardStepRoute";
import workspace from "../bvcomponents/sqft/components/canvas/core/workspace";
import {
  deleteCartItemsThatAreMissingInSQFTCalcData,
  updateQuoteWithSQFTData
} from "../bvcomponents/sqft/helpers/sqftQuotesHelper";
import {prepareSQFTData} from "../bvcomponents/sqft/components/canvas/core/workspace/workspaceDataMapper";
import {getActiveSQFTQuoteIndexFromState} from "../bvcomponents/sqft/helpers/sqftQuoteIndexHelper";
import {syncQuoteSqftCalcDataWithCart} from "../bvcomponents/sqft/helpers/syncQuoteSqftCalcDataWithCart";

/**
 * @param quoteIndex {number}
 * @param fakeLayout {object}
 * @param forLocalStorage {boolean}
 * @param nextVisualizerType {string}
 * @param bvImage {string}
 * @param options {{preservePersistedSQFTData?: boolean}}
 * returns updated quote object
 */
export const getQuoteInfo = async (quoteIndex, fakeLayout = null, forLocalStorage = false, nextVisualizerType,
                                   bvImage = "", options = {}) => {
  const state = store.getState();
  const {quotes} = state.quotesState;
  const {settings} = state.fabricatorState;
  const {canvasDimensions} = state.bathroomVisualizerState;
  const quoteTotalPrice = getTotalPrice(quoteIndex);
  const quoteId = quotes[quoteIndex] && quotes[quoteIndex].quoteId ? quotes[quoteIndex].quoteId : null;
  const imageUrl = quotes[quoteIndex] && quotes[quoteIndex].imageUrl ? quotes[quoteIndex].imageUrl : null;

  const room = getQuoteVisualizerType(quoteIndex, nextVisualizerType);
  // keep old compatability without using different layouts
  const layout = fakeLayout || getCurrentLayout(settings.allowVisualizerSwitching ? room : document.kvConfig.visualizerType);

  // console.log("room", room);
  if (!forLocalStorage && !bvImage) {
    if (document.kvConfig.visualizerType !== room && quotes[quoteIndex] && quotes[quoteIndex].bvImage) {
      bvImage = quotes[quoteIndex].bvImage;
    } else {
      bvImage = await new Promise((resolve) => resolve(getImagePromiseFromLayout(layout, canvasDimensions)))
    }
  }
  const shouldUpdateSqftData = quoteIndex === getActiveSQFTQuoteIndexFromState(state)
    && !forLocalStorage
    && !options.preservePersistedSQFTData
    && shouldPrepareSQFTDataForQuote(quotes[quoteIndex], state);
  const sqFtCalcData = shouldUpdateSqftData
    ? await prepareCurrentSQFTDataForQuote(quotes[quoteIndex])
    : quotes[quoteIndex] ? quotes[quoteIndex].sqFtCalcData || null : null;

  return {
    quoteId, // quoteId - quoteId is passed from the backend in the getBvJobData response(in dashboard edit mode)
    imageUrl, // amazon url
    // ...quotes[activeQuoteIndex], no need for it for now
    data: layout.getActiveFeaturesAsObject(false, true, true),
    leadFeatures: layout.getActiveNames(),
    bvImage,
    layoutType: layout.type,
    room, // prevent overwriting on save
    materialCost: quoteTotalPrice.materialPrice,
    sqFtCalcData,
    installationCost: quoteTotalPrice.installationPrice,
    installationTaxes: quoteTotalPrice.installationTaxes, // installation tax amount
    materialTaxes: quoteTotalPrice.materialTaxes, // material tax amount
    priceTaxes: quoteTotalPrice.priceTaxes, // material tax amount
    staticCanvasImage: getRoomImageFromQtCategory("", quoteIndex),
    isCreatedInSQFT: quotes[quoteIndex] ? quotes[quoteIndex].isCreatedInSQFT : false,
    isVisibleInSQFT: quotes[quoteIndex] ? quotes[quoteIndex].isVisibleInSQFT : true,
    qtCatName: quotes[quoteIndex] ? quotes[quoteIndex].qtCatName : "",
    roomMultiplier: quotes[quoteIndex] ? quotes[quoteIndex].roomMultiplier : "",
  }
};

const prepareCurrentSQFTDataForQuote = async (quote) => {
  try {
    return await prepareSQFTData()
  } catch (error) {
    console.error("Failed to prepare current SQFT data for quote save", error);
    return quote ? quote.sqFtCalcData || null : null
  }
};

const shouldPrepareSQFTDataForQuote = (quote, state) => {
  if (!state.sqftState) {
    return false;
  }

  if (state.sqftState.isSquareFootageEnabled
    || state.sqftState.isSQFTInVisualizer) {
    return true;
  }

  // Quote snapshots can happen while a newly created room is already active,
  // but the mutable SQFT workspace still contains the previous room. Only
  // refresh persisted SQFT data for quotes that already own SQFT data.
  return !!(quote && quote.sqFtCalcData && workspace.isInitialized && !workspace.isEmpty());
};

export const updateCurrentQuote = async (quoteIndex, nextVisualizerType, forLocalStorage = false,
                                         options = {}) => {
  const {quotes, activeQuoteIndex} = store.getState().quotesState;
  const indexToUpdate = Number.isInteger(quoteIndex) ? quoteIndex : activeQuoteIndex;
  const currentQuoteObject = await getQuoteInfo(
    indexToUpdate,
    null,
    forLocalStorage,
    nextVisualizerType,
    "",
    options
  );
  const newQuotes = [...quotes.map((q, qIndex) => (qIndex === indexToUpdate) ? currentQuoteObject : q)];
  if (!forLocalStorage) {
    store.dispatch(setQuotes(newQuotes));
  }
  return newQuotes
};

// /**
//  * @param quotes {array}
//  * @param activeQuoteIndex {number}
//  * returns updated quotes array
//  */
// export const updateAllQuotes = async (quotes, activeQuoteIndex) => {
//   console.log("quotes", quotes);
//   let newQuotes = [];
//   for (const [quoteIndex, quoteObject] of quotes.entries()) {
//     let newQuote = null;
//     if (quoteIndex !== activeQuoteIndex && quoteObject.data) {
//       let fakeLayout = null;
//       const layout = getCurrentLayout();
//       fakeLayout = cloneDeep(layout);
//       fakeLayout.type = quoteObject.data.layout_type;
//       fakeLayout.scene = quoteObject.data.scene;
//       fakeLayout.getFeatures(true).forEach(parentGroup => {
//         parentGroup.layout = fakeLayout;
//         parentGroup.relatedItems.forEach(relItem => {
//           relItem.getParent = () => parentGroup;
//         })
//       });
//       await fakeLayout.selectLayout(layout.type, layout.type, null, layout.scene, null, true);
//       newQuote = await getQuoteInfo(quoteIndex, fakeLayout);
//     } else {
//       newQuote = await getQuoteInfo(quoteIndex);
//     }
//     newQuotes.push(newQuote);
//   }
//   console.log("newQuotes", newQuotes);
//   store.dispatch(setQuotes(newQuotes));
//   return newQuotes
// };

export const setNewActiveQuote = async (index, newQuotes) => {
  if (!newQuotes) {
    newQuotes = store.getState().quotesState.quotes;
  }
  const newQuote = newQuotes[index];
  if (newQuote && (newQuote.layoutType || newQuote.layoutType === 0) && newQuote.data) {
    loaderEnable();
    if (newQuote.room !== document.kvConfig.visualizerType) {
      await switchToNewVisualizerType(newQuote.room, false)
    }
    const layout = getCurrentLayout();
    const bvState = newQuote.data;
    const {wizardIsActive} = store.getState().wizardState;
    if (wizardIsActive) {
      // change selectedWizardValues!
      switchToWizardItem(null, null, null, null, false);
    }
    return layout.setItemsFromBvState(bvState)
        .then(() => {
          loaderDisable();
          store.dispatch(setVisualizerIsChanging(false))
        });
  }
  return Promise.resolve()
};

export const deleteCartItemsFromSQFTCalcData = async (cartItems) => {
  if (!Array.isArray(cartItems) || cartItems.length === 0) {
    return;
  }

  // Group cartItems by currentQuote
  const itemsByQuote = {};
  for (const item of cartItems) {
    if (!item || !item.sqftIds || !item.sqftIds.length) {
      continue;
    }
    if (!itemsByQuote[item.currentQuote]) {
      itemsByQuote[item.currentQuote] = [];
    }
    itemsByQuote[item.currentQuote].push(item);
  }

  const quoteKeys = Object.keys(itemsByQuote);
  if (quoteKeys.length === 0) {
    return;
  }

  const state = store.getState();
  const quotes = state.quotesState.quotes;
  const remainingCartItems = state.cartState.cartItems;
  const updatedQuoteIndexes = [];

  for (const quoteKey of quoteKeys) {
    const quote = quotes[quoteKey];
    if (!quote) {
      continue;
    }

    // Collect all sqftIds for this quote
    const sqftIds = [];
    for (const item of itemsByQuote[quoteKey]) {
      for (const id of item.sqftIds) {
        sqftIds.push(id);
      }
    }

    if (!sqftIds.length) {
      continue;
    }

    // A calculator-derived material can be replaced from the cart without reopening the
    // calculator. Align the saved shapes first so deletion reconciliation does not treat
    // the replacement material as a missing cart item.
    const syncedQuotes = syncQuoteSqftCalcDataWithCart(
      quotes,
      Number(quoteKey),
      remainingCartItems,
      getCurrentLayout(quote.room)
    );
    const syncedQuote = syncedQuotes[quoteKey] || quote;

    workspace.import(syncedQuote.sqFtCalcData);
    for (const id of sqftIds) {
      workspace.removeFeatureBySQFTId(id);
    }
    updatedQuoteIndexes.push(Number(quoteKey));

    const sqftCalcData = await prepareSQFTData();
    updateQuoteWithSQFTData(sqftCalcData, Number(quoteKey));
  }

  if (updatedQuoteIndexes.length > 0) {
    deleteCartItemsThatAreMissingInSQFTCalcData(updatedQuoteIndexes);
  }
};
